import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum ActiveProjectKey {
    Datasets = 'datasets',
    Workflows = 'workflows',
    TransferStatuses = 'transferStatuses',
    Containers = 'containers',
    JobScripts = 'jobScripts',
    Hpc = 'hpc',
    DdiDashboard = 'ddiDashboard',
    WorkflowsDashboard = 'workflowsDashboard',
    CyclopsDashboard = 'cyclopsDashboard',
}

interface ActiveProjectsState {
    [ActiveProjectKey.Datasets]: string | undefined;
    [ActiveProjectKey.Workflows]: string | undefined;
    [ActiveProjectKey.TransferStatuses]: string | undefined;
    [ActiveProjectKey.Containers]: string | undefined;
    [ActiveProjectKey.JobScripts]: string | undefined,
    [ActiveProjectKey.Hpc]: string | undefined,
    [ActiveProjectKey.DdiDashboard]: string | undefined;
    [ActiveProjectKey.WorkflowsDashboard]: string | undefined;
}

const localStorageKeyMap: Record<ActiveProjectKey, string> = {
    [ActiveProjectKey.Datasets]: 'selectedDatasetsTabProject',
    [ActiveProjectKey.Workflows]: 'selectedWorkflowsTabProject',
    [ActiveProjectKey.TransferStatuses]: 'selectedTransferStatusesTabProject',
    [ActiveProjectKey.Containers]: 'selectedContainersTabProject',
    [ActiveProjectKey.JobScripts]: 'selectedJobScriptsTabProject',
    [ActiveProjectKey.Hpc]: 'selectedHpcTabProject',
    [ActiveProjectKey.DdiDashboard]: 'selectedDDIDashboardTabProject',
    [ActiveProjectKey.WorkflowsDashboard]: 'selectedWorkflowsDashboardTabProject',
    [ActiveProjectKey.CyclopsDashboard]: 'selectedCyclopsDashboardTabProject',
};

const initialState: ActiveProjectsState = Object.values(ActiveProjectKey).reduce((acc, key) => {
    acc[key] = localStorage.getItem(localStorageKeyMap[key as ActiveProjectKey]) ?? undefined;
    return acc;
}, {} as ActiveProjectsState);

const activeProjectsSlice = createSlice({
    name: 'activeProjects',
    initialState,
    reducers: {
        setActiveProject: (
            state,
            action: PayloadAction<{ key: ActiveProjectKey; project: string | undefined }>
        ) => {
            const { key, project } = action.payload;
            state[key] = project;
            if (project) {
                localStorage.setItem(localStorageKeyMap[key], project);
            } else {
                localStorage.removeItem(localStorageKeyMap[key]);
            }
        },
        resetActiveProject: (state, action: PayloadAction<ActiveProjectKey>) => {
            const key = action.payload;
            state[key] = undefined;
            localStorage.removeItem(localStorageKeyMap[key]);
        },
    },
});

export const { setActiveProject, resetActiveProject } = activeProjectsSlice.actions;
export default activeProjectsSlice.reducer;
